import React from 'react'
import PropTypes from 'prop-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

function applyTextReplacements (node, replacements) {
  if (!node) {
    return node
  }

  if (node.nodeType === 'text' && typeof node.value === 'string') {
    let value = node.value
    for (const [placeholder, replacement] of Object.entries(replacements)) {
      if (replacement === undefined || replacement === null) {
        continue
      }
      if (value.includes(placeholder)) {
        value = value.split(placeholder).join(String(replacement))
      }
    }
    if (value !== node.value) {
      return { ...node, value }
    }
    return node
  }

  if (node.content && Array.isArray(node.content)) {
    const newContent = node.content.map((child) => applyTextReplacements(child, replacements))
    if (newContent.some((child, i) => child !== node.content[i])) {
      return { ...node, content: newContent }
    }
  }

  return node
}

/**
 * Contentful Rich Text component that uses the official @contentful/rich-text-react-renderer
 * to parse and render Contentful rich text data as JSX
 *
 * @param {Object} props - Component props
 * @param {Object} props.richTextData - The Contentful rich text data
 * @param {Object} props.options - Optional renderer options for customizing output.
 * @param {Object} [props.textReplacements] - Map of substring placeholders to values (e.g. `{ '{number}': 60 }`) applied to text nodes before render.
 * @returns {JSX.Element} The parsed rich text as JSX
 */
export const ContentfulRichText = ({ richTextData, options, textReplacements, ...restProps }) => {
  if (!richTextData) {
    return null
  }

  const dataToRender =
    textReplacements && Object.keys(textReplacements).length > 0
      ? applyTextReplacements(richTextData, textReplacements)
      : richTextData

  const renderedContent = documentToReactComponents(dataToRender, options || {})

  if (!renderedContent) {
    return null
  }

  return (
    <div {...restProps}>
      {renderedContent}
    </div>
  )
}

ContentfulRichText.propTypes = {
  richTextData: PropTypes.object,
  options: PropTypes.object,
  textReplacements: PropTypes.object
}

export default ContentfulRichText
